import React, { useRef, useEffect } from 'react';
import { useSetting } from '../../utils/AppSettingsContext';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';

export default function Calendar({
  workOrders = [],
  events,
  onEventClick,
  onEventDoubleClick,
  onDateSelect,
  onDateRangeChange,
  onEventDrop,
  onEventResize,
  eventContentRenderer,
  eventClassNameResolver,
  editable = true,
  selectable = true,
}) {
  const calendarRef = useRef();

  const shiftStartTime = useSetting('shift_start_time', '08:00:00');

  const formatLocationLabel = (workOrder) => {
    const hasAsset = workOrder.assetId != null;
    const hasArea = workOrder.areaId != null;

    if (hasAsset) {
      return workOrder.asset?.assetNumber || '';
    }

    if (hasArea) {
      const areaCode = workOrder.area?.areaCode || '';
      const areaName =
        workOrder.area?.name || '';
      return [areaCode, areaName].filter(Boolean).join(' ').trim();
    }

    return '';
  };

  const transformWorkOrdersToEvents = () =>
    workOrders.map((wo) => ({
      id: wo.id,
      title: wo.workOrderTitle,
      start: wo.scheduledAt,
      end: wo.dueDate,
      allDay: false,
      extendedProps: wo,
    }));

  const renderEventContent = (eventInfo) => {
    if (typeof eventContentRenderer === 'function') {
      return eventContentRenderer(eventInfo);
    }

    const workOrder = eventInfo.event.extendedProps;
    const locationLabel = formatLocationLabel(workOrder);
    const title = workOrder.workOrderTitle || '';

    return (
      <>
        <b>#{workOrder.workOrderNumber}</b>
        {locationLabel ? ` (${locationLabel})` : ''}
        {title ? ` ${title}` : ''}
      </>
    );
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi?.();
    const handler = (e) => {
      if (e.target.getAttribute('data-bs-target') === '#calendar') {
        setTimeout(() => {
          calendarApi?.updateSize();
          if (calendarApi) {
            const view = calendarApi.view;
            onDateRangeChange?.(view.activeStart, view.activeEnd); // call the new prop
          }
        }, 50);
      }
    };
    document.addEventListener('shown.bs.tab', handler);
    return () => document.removeEventListener('shown.bs.tab', handler);
  }, []);

  const handleDatesSet = (arg) => {
    onDateRangeChange?.(arg.start, arg.end);
    const calendarApi = calendarRef.current?.getApi?.();
    if (arg.view?.type === 'timeGridDay' || arg.view?.type === 'timeGridWeek') {
      calendarApi?.scrollToTime?.(shiftStartTime);
    }
  };

  return (
    <div className="align-self-start">
      <div style={{ height: 'calc(100vh - 239px)' }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          headerToolbar={{
            left: 'title',
            center: 'timeGridDay,timeGridWeek,dayGridMonth',
            right: 'prev,next today'
          }}
          initialView="dayGridMonth"
          editable={editable}
          selectable={selectable}
          selectMirror={true}
          dayMaxEvents={true}
          allDayText=""
          height="100%"
          events={Array.isArray(events) ? events : transformWorkOrdersToEvents()}
          eventClassNames={(info) => {
            if (typeof eventClassNameResolver === 'function') {
              const classes = eventClassNameResolver(info.event.extendedProps, info);
              return Array.isArray(classes) ? classes : [];
            }
            return [];
          }}
          eventDidMount={(info) => {
            if (typeof onEventDoubleClick === 'function') {
              info.el.ondblclick = () => onEventDoubleClick(info.event.extendedProps);
            }

            if (typeof eventClassNameResolver === 'function') {
              info.el.style.setProperty('border', 'none', 'important');
              info.el.style.setProperty('padding', 0, 'important');
              const mainEl = info.el.querySelector('.fc-event-main');
              if (mainEl) {
                mainEl.style.setProperty('padding-left', '4px', 'important');
                mainEl.style.setProperty('padding-right', '4px', 'important');
              }
            }

            const bootstrapName = info.event.extendedProps.status?.colours?.bootstrapName || 'primary';
            const bg = `var(--bs-${bootstrapName}-bg-subtle)`; // Bootstrap subtle background
            const text = `var(--bs-${bootstrapName})`;

            if (typeof eventClassNameResolver === 'function') {
              return;
            }

            // Outer wrapper
            info.el.style.setProperty('background-color', bg, 'important');
            info.el.style.setProperty('border-left-color', text, 'important');
            info.el.style.setProperty('border-left-style', 'solid', 'important');
            info.el.style.setProperty('border-left-width', '4px', 'important');
            info.el.style.setProperty('color', text, 'important');
            info.el.style.setProperty('padding', 0, 'important');

            // Inner content (timeGrid/week/day views)
            const mainEl = info.el.querySelector('.fc-event-main');
            if (mainEl) {
              mainEl.style.setProperty('background-color', bg, 'important');
              mainEl.style.setProperty('color', text, 'important');
            }
          }}

          select={(info) => onDateSelect?.(info)}
          eventClick={(info) => onEventClick?.(info.event.extendedProps)}
          eventDrop={(info) => onEventDrop?.(info)}
          eventResize={(info) => onEventResize?.(info)}
          eventContent={renderEventContent}
          datesSet={handleDatesSet} // track visible range
        />
      </div>
    </div>
  );
}
